import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import type { AreaDescriptionDocType } from '@ast-data/shared';
import { useProjectDocs } from '../../db/useScopedDocs';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { areaDescriptionLocation } from '../findings/areaDescriptionLabel';
import FieldCardList, { type FieldCardRow } from './FieldCardList';
import type { FieldSortOption } from './fieldListSort';

const SORT = {
    listId: 'areaDescriptions',
    options: [
        { key: 'location', labelId: 'field.sort.location' },
        { key: 'updatedAt', labelId: 'field.sort.updatedAt', direction: 'desc' }
    ] satisfies FieldSortOption[]
};

/**
 * Touch-first list of a project's Flächenbeschreibungen, with fast add. Selecting
 * one (or Add) opens the office fullscreen dialog over this list — see
 * [[field-reuses-office-dialogs]].
 */
export default function FieldAreaDescriptionsPage() {
    const intl = useIntl();
    const navigate = useNavigate();
    const { projectId = '' } = useParams<{ projectId: string }>();

    const { results: areaDescriptions } =
        useProjectDocs<AreaDescriptionDocType>('area_descriptions', projectId);

    const ability = useAbility();
    const canCreate = ability.can(
        'create',
        scopedSubject('AreaDescription', { projectId })
    );

    const [search, setSearch] = useState('');

    const rows = useMemo<FieldCardRow[]>(
        () =>
            areaDescriptions.map((description) => {
                const location = areaDescriptionLocation(description);
                return {
                    id: description.id,
                    title: intl.formatMessage(
                        { id: 'areaDescription.label' },
                        { location }
                    ),
                    subtitle: description.editor || undefined,
                    sortKey: location,
                    sortValues: {
                        location,
                        updatedAt: description.updatedAt
                    },
                    searchText: [location, description.editor]
                        .filter(Boolean)
                        .join(' ')
                };
            }),
        [areaDescriptions, intl]
    );

    const handleSelect = useCallback(
        (id: string) =>
            navigate(`/field/${projectId}/area-descriptions/${id}/edit`),
        [navigate, projectId]
    );

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <FieldCardList
                rows={rows}
                onSelect={handleSelect}
                emptyMessageId="field.areaDescriptions.empty"
                noMatchesMessageId="field.areaDescriptions.noMatches"
                sort={SORT}
                search={search}
                onSearchChange={setSearch}
                toolbar={
                    canCreate ? (
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={() =>
                                navigate(
                                    `/field/${projectId}/area-descriptions/new`
                                )
                            }
                        >
                            <FormattedMessage id="areaDescription.add" />
                        </Button>
                    ) : undefined
                }
            />
            {/* The Flächenbeschreibung dialog routes render here, over the list. */}
            <Outlet />
        </Box>
    );
}
